import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  ArrowUpRight,
  DotsThreeVertical,
  PencilSimple,
  Trash,
  Copy,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { resolveIcon } from "./IconPicker";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "./ui/context-menu";

function getDomain(url) {
  try {
    const u = /^https?:\/\//i.test(url) ? url : "https://" + url;
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function TileMenuItems({ tile, onOpen, onEdit, onDuplicate, onDelete, ItemComponent, SeparatorComponent }) {
  return (
    <>
      <ItemComponent
        data-testid={`menu-open-${tile.tile_id}`}
        onClick={() => onOpen(tile)}
        className="cursor-pointer"
      >
        <ArrowSquareOut size={14} className="mr-2" /> Abrir
      </ItemComponent>
      <ItemComponent
        data-testid={`menu-edit-${tile.tile_id}`}
        onClick={() => onEdit(tile)}
        className="cursor-pointer"
      >
        <PencilSimple size={14} className="mr-2" /> Editar
      </ItemComponent>
      <ItemComponent
        data-testid={`menu-duplicate-${tile.tile_id}`}
        onClick={() => onDuplicate(tile)}
        className="cursor-pointer"
      >
        <Copy size={14} className="mr-2" /> Duplicar
      </ItemComponent>
      <SeparatorComponent />
      <ItemComponent
        data-testid={`menu-delete-${tile.tile_id}`}
        onClick={() => onDelete(tile)}
        className="cursor-pointer text-red-400 focus:text-red-400 focus:bg-red-500/10"
      >
        <Trash size={14} className="mr-2" /> Eliminar
      </ItemComponent>
    </>
  );
}

function TileCard({ tile, view, onEdit, onDelete, onDuplicate, isDragging }) {
  const Icon = resolveIcon(tile.icon);
  const isRich = tile.style === "rich" && (tile.preview_image || tile.preview_title);
  const sizeClass = (() => {
    if (view === "list") return "h-[88px] w-full";
    if (tile.size === "2x2") return "col-span-2 row-span-2";
    if (tile.size === "2x1") return "col-span-2 row-span-1";
    return "col-span-1 row-span-1";
  })();

  const openTile = (t) => {
    window.open(t.url, "_blank", "noopener,noreferrer");
  };

  const dropdownContent = (
    <DropdownMenuContent
      align="end"
      className="bg-deck-sidebar border-white/10 text-white min-w-[160px]"
      onClick={(e) => e.stopPropagation()}
    >
      <TileMenuItems
        tile={tile}
        onOpen={openTile}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        ItemComponent={DropdownMenuItem}
        SeparatorComponent={DropdownMenuSeparator}
      />
    </DropdownMenuContent>
  );

  const contextContent = (
    <ContextMenuContent className="bg-deck-sidebar border-white/10 text-white min-w-[180px]">
      <TileMenuItems
        tile={tile}
        onOpen={openTile}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        ItemComponent={ContextMenuItem}
        SeparatorComponent={ContextMenuSeparator}
      />
    </ContextMenuContent>
  );

  // ---------- LIST VIEW ----------
  if (view === "list") {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            data-testid={`tile-${tile.tile_id}`}
            className={`deck-tile relative ${sizeClass} flex items-center gap-4 px-5 py-4 rounded-2xl overflow-hidden ${
              isDragging ? "is-dragging" : ""
            }`}
            style={{
              backgroundColor: isRich ? (tile.theme_color || tile.color) : tile.color,
              color: tile.text_color,
            }}
          >
            <div className="w-12 h-12 rounded-xl bg-black/20 flex items-center justify-center shrink-0 overflow-hidden">
              {isRich && tile.favicon ? (
                <img src={tile.favicon} alt="" className="w-7 h-7 object-contain" />
              ) : tile.image_url ? (
                <img src={tile.image_url} alt="" className="w-8 h-8 rounded" />
              ) : (
                <Icon size={26} weight="fill" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-base truncate">{tile.name}</div>
              <div className="text-xs font-mono opacity-70 truncate">{tile.url}</div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`tile-menu-${tile.tile_id}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  className="w-8 h-8 rounded-lg bg-black/20 hover:bg-black/40 flex items-center justify-center"
                  title="Opciones"
                >
                  <DotsThreeVertical size={16} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              {dropdownContent}
            </DropdownMenu>
            <a
              href={tile.url}
            draggable={false}
              target="_blank"
              rel="noopener noreferrer"
              onPointerDown={(e) => e.stopPropagation()}
              className="w-9 h-9 rounded-lg bg-black/20 hover:bg-black/40 flex items-center justify-center"
              title="Abrir"
            >
              <ArrowUpRight size={16} weight="bold" />
            </a>
          </div>
        </ContextMenuTrigger>
        {contextContent}
      </ContextMenu>
    );
  }

  // ---------- GRID — RICH STYLE ----------
  if (isRich) {
    const domain = getDomain(tile.url);
    const isLarge = tile.size === "2x2";
    const isWide = tile.size === "2x1";
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <a
            href={tile.url}
            draggable={false}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`tile-${tile.tile_id}`}
            className={`deck-tile group relative ${sizeClass} flex flex-col justify-end rounded-3xl overflow-hidden ${
              isDragging ? "is-dragging" : ""
            }`}
            style={{ backgroundColor: tile.theme_color || tile.color || "#1A1C29" }}
          >
            {tile.preview_image && (
              <img
                src={tile.preview_image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5 z-[1]" />

            {/* Top right arrow */}
            <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/40 flex items-center justify-center backdrop-blur-sm z-[3]">
              <ArrowUpRight size={13} weight="bold" className="text-white" />
            </div>

            {/* Top left favicon */}
            {tile.favicon && (
              <div className="absolute top-3 left-3 w-7 h-7 rounded-lg bg-white/95 flex items-center justify-center z-[3] overflow-hidden shadow-lg">
                <img src={tile.favicon} alt="" className="w-4 h-4 object-contain" />
              </div>
            )}

            {/* Dots menu (always visible at low opacity, full on hover) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`tile-menu-${tile.tile_id}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="absolute top-3 left-12 w-7 h-7 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center backdrop-blur-sm opacity-50 group-hover:opacity-100 focus:opacity-100 transition-opacity z-[3]"
                  title="Opciones"
                >
                  <DotsThreeVertical size={13} weight="bold" className="text-white" />
                </button>
              </DropdownMenuTrigger>
              {dropdownContent}
            </DropdownMenu>

            {/* Bottom content */}
            <div className="relative p-3 z-[2]">
              <div
                className={`font-heading font-bold text-white leading-tight drop-shadow ${
                  isLarge
                    ? "text-lg line-clamp-3"
                    : isWide
                    ? "text-base line-clamp-2"
                    : "text-sm line-clamp-2"
                }`}
              >
                {tile.preview_title || tile.name}
              </div>
              {domain && (
                <div className="text-[9px] font-mono uppercase tracking-[0.1em] text-white/70 mt-1 truncate">
                  {domain}
                </div>
              )}
            </div>
          </a>
        </ContextMenuTrigger>
        {contextContent}
      </ContextMenu>
    );
  }

  // ---------- GRID — ICON STYLE ----------
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <a
          href={tile.url}
            draggable={false}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`tile-${tile.tile_id}`}
          className={`deck-tile group relative ${sizeClass} flex flex-col justify-between p-4 rounded-3xl ${
            isDragging ? "is-dragging" : ""
          }`}
          style={{ backgroundColor: tile.color, color: tile.text_color }}
        >
          <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/30 flex items-center justify-center backdrop-blur-sm z-[2]">
            <ArrowUpRight size={13} weight="bold" />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`tile-menu-${tile.tile_id}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="absolute top-3 left-3 w-7 h-7 rounded-full bg-black/40 hover:bg-black/70 flex items-center justify-center backdrop-blur-sm opacity-50 group-hover:opacity-100 focus:opacity-100 transition-opacity z-[2]"
                title="Opciones"
              >
                <DotsThreeVertical size={13} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            {dropdownContent}
          </DropdownMenu>

          <div className="flex-1 flex items-center justify-center">
            {tile.image_url ? (
              <img
                src={tile.image_url}
                alt=""
                className="max-w-[56%] max-h-[56%] object-contain"
              />
            ) : (
              <Icon
                size={tile.size === "2x2" ? 96 : 52}
                weight="fill"
                className="drop-shadow-[0_4px_8px_rgba(0,0,0,0.3)]"
              />
            )}
          </div>

          <div
            className="font-mono text-[11px] sm:text-xs font-bold uppercase tracking-[0.08em] truncate"
            style={{ color: tile.text_color }}
          >
            {tile.name}
          </div>
        </a>
      </ContextMenuTrigger>
      {contextContent}
    </ContextMenu>
  );
}

function SortableTile({ tile, view, onEdit, onDelete, onDuplicate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.tile_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const wrapperClass =
    view === "grid"
      ? tile.size === "2x2"
        ? "col-span-2 row-span-2"
        : tile.size === "2x1"
        ? "col-span-2 row-span-1"
        : ""
      : "";

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${wrapperClass} touch-none select-none cursor-grab active:cursor-grabbing h-full w-full [&>*]:h-full [&>*]:w-full`}
    >
      <TileCard
        tile={tile}
        view={view}
        onEdit={onEdit}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        isDragging={isDragging}
      />
    </div>
  );
}

function AddTileCard({ onClick, view }) {
  const className = view === "list" ? "h-[88px] w-full" : "col-span-1 row-span-1";
  return (
    <button
      data-testid="add-tile-btn"
      onClick={onClick}
      className={`${className} group rounded-3xl border-2 border-dashed border-white/15 bg-white/[0.02] hover:border-[#2563EB]/60 hover:bg-[#2563EB]/8 flex flex-col items-center justify-center gap-2 transition-all`}
    >
      <div className="w-12 h-12 rounded-2xl bg-white/5 group-hover:bg-[#2563EB]/15 flex items-center justify-center transition">
        <Plus size={24} weight="bold" className="text-white/40 group-hover:text-[#2563EB] transition" />
      </div>
      <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 group-hover:text-[#2563EB] transition">
        Añadir botón
      </span>
    </button>
  );
}

export default function DeckGrid({
  tiles,
  view,
  onAddTile,
  onEditTile,
  onDeleteTile,
  onDuplicateTile,
}) {
  if (tiles.length === 0) {
    return (
      <div className="max-w-md mx-auto mt-12 text-center">
        <div className="text-2xl font-heading font-bold mb-2">
          Esta plataforma está vacía
        </div>
        <div className="text-white/50 mb-8 font-mono text-sm">
          Añade tu primer botón para acceder a tus apps con un click.
        </div>
        <button
          onClick={onAddTile}
          data-testid="empty-add-tile-btn"
          className="px-6 py-3 rounded-2xl bg-[#2563EB] text-white font-heading font-bold deck-tile inline-flex items-center gap-2"
        >
          <Plus size={18} weight="bold" /> Añadir botón
        </button>
      </div>
    );
  }

  const containerClass =
    view === "grid"
      ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5 auto-rows-[140px] md:auto-rows-[160px]"
      : "flex flex-col gap-3";

  return (
    <SortableContext
      items={tiles.map((t) => t.tile_id)}
      strategy={view === "grid" ? rectSortingStrategy : verticalListSortingStrategy}
    >
      <div className={containerClass} data-testid="deck-grid">
        {tiles.map((tile) => (
          <SortableTile
            key={tile.tile_id}
            tile={tile}
            view={view}
            onEdit={onEditTile}
            onDelete={onDeleteTile}
            onDuplicate={onDuplicateTile}
          />
        ))}
        <AddTileCard onClick={onAddTile} view={view} />
      </div>
    </SortableContext>
  );
}

export { TileCard };
